//#region import libraries
import React, { useEffect, useState } from 'react'

//styles
import { Colors, Spacing } from '_styles'

//redux
import { useDispatch, useSelector } from 'react-redux'
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { addSeasonalVariance, selectAddSeasonalVariancesStatus, selectSeasonalVariances, resetStatus, checkDuplication, selectCheckDuplicationResult, selectCheckDuplicationStatus } from '_redux/slices/prm-bookings/seasonal-variance'

//bootstrap
import Modal from 'react-modal';
import { Button, Form, Row } from 'react-bootstrap'

//custom components
import { DateRangePicker, Flex, Subtitle, Title } from '@tremor/react'
import { CustomSpinner } from '_components/atoms'
import moment from 'moment';

Modal.setAppElement('#root');   // removes warning of modal from log
//#endregion

const SeasonVarianceForm = (props) => {

    //#region redux
    const dispatch = useDispatch()

    const dbUser = useSelector(selectFirebaseInfo);
    //status flags
    const addSeasonVarianceStatus = useSelector(selectAddSeasonalVariancesStatus);
    const checkDuplicationStatus = useSelector(selectCheckDuplicationStatus);

    const seasonVariances = useSelector(selectSeasonalVariances);

    const duplicationResult = useSelector(selectCheckDuplicationResult)
    //#endregion

    //#region local state
    const [isLoading, setIsLoading] = useState(false);
    const [date, setDate] = useState([new Date(), new Date()]);
    const [multiplier, setMultiplier] = useState(0);
    const [disableAddingSV, setDisableAddingSV] = useState(true);
    const [tag, setTag] = useState("");
    const [errors, setErrors] = useState("");
    const [checkSeasonalVariance, setCheckSeasonalVariance] = useState(false);
    const [newSeasonalVariance, setNewSeasonalVariance] = useState({})

    //#endregion

    //#region useEffect

    useEffect(() => {
        if (addSeasonVarianceStatus === "loading") {
            setIsLoading(true)
        }
        if (addSeasonVarianceStatus === "succeeded") {
            dispatch(resetStatus())
            setNewSeasonalVariance({})
            handleCloseModal()
        }

    }, [addSeasonVarianceStatus, checkDuplicationStatus])

    useEffect(() => {
        if (multiplier !== 0 && multiplier.includes("%") && date[0] && date[1] && tag.length > 1) {
            setDisableAddingSV(false)
        }
        setErrors({})
    }, [multiplier, date, tag]);

    useEffect(() => {
        if (checkSeasonalVariance) {
            if (duplicationResult && duplicationResult.duplicationFound) {
                setErrors({ msg: duplicationResult.message, errorType: duplicationResult.errorType })
            } else {
                setCheckSeasonalVariance(false);
                dispatch(addSeasonalVariance(newSeasonalVariance))
            }
        }
    }, [duplicationResult])


    //#endregion

    //#region functions 
    const convertToDecimal = () => {
        const numericValue = parseFloat(multiplier.replace('%', '')); // Remove '%' and convert to a number
        if (!isNaN(numericValue)) {
            const decimalValue = numericValue / 100; // Convert to decimal
            return decimalValue
        }
    };

    const handleAddSV = () => {

        let temp = [];
        // for airport without season variances, it  will be empty object only
        if (!Object.isObjectEmpty(seasonVariances)) {
            temp = [...seasonVariances.seasonalAdjustments];
        }

        let decimalMultiplier = convertToDecimal(multiplier) // convert percentage val into a decimal to be saved

        temp.push({
            startDate: moment(new Date(date[0])).format().split("T")[0],
            endDate: moment(new Date(date[1])).format().split("T")[0],
            multiplier: decimalMultiplier,
            tag,
        })

        let postObject = { ...seasonVariances, seasonalAdjustments: temp }

        //if first time to add seasonal variance, then add iata to body since seasonVariances is empty object
        if (Object.isObjectEmpty(seasonVariances)) {
            postObject["iata"] = dbUser.details.authority;
        }

        //save postObject into state before check if there is a duplicate or not
        setNewSeasonalVariance(postObject)
        setCheckSeasonalVariance(true)
        // data to check duplication : start date, end date, tag for iata
        const data = {
            iata: dbUser.details.authority,
            startDate: moment(new Date(date[0])).format().split("T")[0],
            endDate: moment(new Date(date[1])).format().split("T")[0],
            tag
        }
        dispatch(checkDuplication(data))

    }

    const handleCloseModal = () => {
        props.closeModal();
    }

    const handleInputBlur = () => {
        if (multiplier !== '' && !isNaN(multiplier)) {
            setMultiplier(`${multiplier}%`); // Append '%' when the input loses focus          
        } else {
            setErrors({ msg: "Please enter a numeric value", errorType: "MULTIPLIER" })
        }
    };

    //#endregion

    return (
        <div style={{ padding: Spacing.SCALE_12, opacity: isLoading ? 0.3 : 1.0 }}>

            {/* title */}
            <div style={{ marginLeft: Spacing.SCALE_12, marginTop: Spacing.SCALE_12 }}            >
                <Title>{"Add Seasonal Adjustments"}</Title>
                <Subtitle>{`To create a seasonal variance enter the information below and click Add`}</Subtitle>
            </div>

            <hr color={Colors.PRM_LIGHT_BLUE} />
            {/*body of modal*/}
            <div style={{ marginLeft: Spacing.SCALE_12, marginRight: Spacing.SCALE_12 }}>

                {/* date range */}
                <Row>
                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, display: "flex" }}>Date of Range</Form.Label>

                    <DateRangePicker
                        onValueChange={(value) => { setDate(value) }}
                        value={date}
                        enableDropdown={false}
                        maxWidth="max-w-lg"
                        dropdownPlaceholder={"Today"}
                    />
                    {errors && errors.errorType === "DATE" && <p style={{ color: "red" }}>{errors.msg}</p>}
                </Row>

                {/* percentage variance */}
                <Row className='mt-3'>
                    <Form.Label className="mt-3" style={{ color: Colors.PRM_LIGHT_BLUE }}>Percentage Variance</Form.Label>
                    <Form.Control
                        value={multiplier}
                        onChange={(e) => { setMultiplier(e.target.value) }}
                        onBlur={handleInputBlur}
                        type="text"
                        style={{ borderColor: 'null' }}
                    />
                    {errors && errors.errorType === "MULTIPLIER" && <p style={{ color: "red" }}>{errors.msg}</p>}
                </Row>

                {/* season tag */}
                <Row className='mt-3'>
                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Season Tag</Form.Label>

                    <Form.Control
                        onChange={(e) => { setTag(e.target.value) }}
                        type="name"
                        placeholder="Enter season tag "
                        value={tag}
                    />
                    {errors && errors.errorType === "TAG" && <p style={{ color: "red" }}>{errors.msg}</p>}

                </Row>

                {/* buttons */}
                <div className='mt-5'>
                    <Flex>
                        <Button variant="danger" onClick={handleCloseModal}> {"Cancel"} </Button>
                        <Button onClick={handleAddSV} variant={"primary"} style={{ marginLeft: Spacing.SCALE_8 }} disabled={disableAddingSV}> {isLoading ? <CustomSpinner /> : "Add Seasonal Variance"} </Button>
                    </Flex>
                </div>

            </div>

        </div>
    )
}

export default SeasonVarianceForm
